// ===================================================================================================
// ResultsTile.jsx
// ---------------------------------------------------------------------------------------------------
// This file exposes a single, self-contained "tile" that renders one saved or searched ocean-freight
// offer in the compact 5-column layout, with an expandable details section (containers + totals).
// The tile is intentionally decoupled from lists so it can be reused to render a single saved offer.
// ===================================================================================================

import React, { useState } from "react";
import PropTypes from "prop-types";
import { MButton } from "src/M_Components";

// Shared responsive styles for panel + tiles
import "./ResultsPanel.css";

//==========================================
// tiny money formatter with pt-PT rules (2 decimals); nullish -> 0
//==================================
const fmtMoney = (n) =>
  new Intl.NumberFormat("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n ?? 0));

//==========================================
// turn an ISO string into "YYYY-MM-DD HH:mm"; invalid -> "-"
//==================================
const fmtDateTime = (s) => {
  if (!s) return "-"; // no input, no output
  const d = new Date(s); // try to parse
  if (Number.isNaN(d.getTime())) return "-"; // invalid ISO => dash
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`; // stable output format
};

//==========================================
// compute final value in target currency; multiply when both are present
//==================================
const computeTargetFinal = (usd, rate) => {
  const u = Number(usd ?? 0);
  const r = Number(rate ?? 0);
  return u && r ? u * r : 0; // only multiply if both are truthy numeric
};

//==========================================
// One tile for one offer. Handles expand/collapse for its own details and fires onSave.
// `viewMode` (optional): when true, hides the Save button (read-only tile).
//==================================
export default function ResultsTile({
  offer,
  index,
  t,
  onSave,
  viewMode = false,
}) {
  // local "open" state keeps the expanded details for this tile only
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((v) => !v); // dumb toggle is enough for single tile

  // pre-calc some display flags and numbers so the JSX stays readable
  const totalUsd = Number(
    offer?.finalWithMarkupUsd ?? offer?.totalFinalUsd ?? 0
  );
  const targetExplicit = Number(offer?.finalInTargetAfterMarkup ?? 0);
  const targetComputed = computeTargetFinal(totalUsd, offer?.usdToTargetRate);
  const targetFinal =
    targetExplicit > 0
      ? targetExplicit
      : targetComputed > 0
      ? targetComputed
      : 0;
  const targetCode = offer?.targetCurrencyCode || "";
  const hasTarget = targetFinal > 0 && !!targetCode;

  const hasBook = !!offer?.bookNowLink;
  const isActive = offer?.isRateValid === true;
  const originServ = offer?.originServiceType || "CY";
  const destServ = offer?.destinationServiceType || "CY";

  return (
    <div className="results-tile-root" key={index}>
      {/* Compact header:
          - Desktop: 5-col grid como o original
          - Mobile: linhas conforme especificação (logo + Save, nome+estado + totais, origem/destino, serviço/dias+Book, toggle) */}
      <div className="results-tile-header-grid">
        {/* Provider status + name (fica na col 1 em desktop; linha 2 esq em mobile) */}
        <div className="results-tile-provider">
          {/* small status chip */}
          <span
            className={`results-tile-status ${
              isActive
                ? "results-tile-status--active"
                : "results-tile-status--inactive"
            }`}
          >
            {isActive ? t("Active") || "Active" : t("Inactive") || "Inactive"}
          </span>

          {/* provider label in red */}
          <div className="results-tile-provider-name">
            {offer?.providerName || t("Unknown provider")}
          </div>
        </div>

        {/* Logo (desktop: fica junto ao provider; mobile: linha 1 esquerda) */}
        <div className="results-tile-logo">
          {offer?.mainCarrierLogoUrl ? (
            <img
              src={offer.mainCarrierLogoUrl}
              alt={offer?.providerName || "logo"}
              className="results-tile-logo-img"
            />
          ) : (
            <div className="results-tile-logo-placeholder">{t("No logo")}</div>
          )}
        </div>

        {/* Origin */}
        <div className="results-tile-origin">
          <div className="results-tile-label-muted">{t("Origin")}</div>
          <div className="results-tile-strong">
            {offer?.originPortName || offer?.originLocationName || "-"}
          </div>
          <div className="results-tile-medium">
            {fmtDateTime(offer?.departureUtc)}
          </div>
          <div className="results-tile-small">{originServ}</div>
        </div>

        {/* Arrival */}
        <div className="results-tile-arrival">
          <div className="results-tile-label-muted">{t("Arrival")}</div>
          <div className="results-tile-strong">
            {offer?.destinationPortName ||
              offer?.destinationLocationName ||
              "-"}
          </div>
          <div className="results-tile-medium">
            {fmtDateTime(offer?.arrivalUtc)}
          </div>
          <div className="results-tile-small">{destServ}</div>
        </div>

        {/* Transit, Service Type (desktop col 4; mobile linha 4 esq) */}
        <div className="results-tile-transit">
          <div>
            <span className="results-tile-label-muted">
              {t("Transit days")}:
            </span>{" "}
            <b>{offer?.transitDays ?? "-"}</b>
          </div>
          <div>
            <span className="results-tile-label-muted">
              {t("Service Type")}:
            </span>{" "}
            <b>{offer?.serviceType || "-"}</b>
          </div>
        </div>

        {/* Book link (desktop: na mesma coluna da transit, em baixo; mobile: linha 4 dir) */}
        <div className="results-tile-book">
          {hasBook && (
            <a
              href={offer.bookNowLink}
              target="_blank"
              rel="noreferrer"
              className="results-tile-book-link"
            >
              <i className="pi pi-external-link" /> {t("Book now")}
            </a>
          )}
        </div>

        {/* Save + totals (desktop col 5, alinhado à direita; mobile: linha 1/2 dir) */}
        <div className="results-tile-save">
          {/* onSave is injected by parent so we can reuse the tile everywhere.
              In viewMode we hide the Save button but keep the totals. */}
          {!viewMode && typeof onSave === "function" && (
            <div className="results-tile-save-button-wrapper">
              <MButton
                label={t("Save")}
                icon={<i className="pi pi-save" />}
                onClick={() => onSave(offer)}
              />
            </div>
          )}

          <div className="results-tile-total results-tile-total--usd">
            <div className="results-tile-label-muted">{t("Total (USD)")}</div>
            <div className="results-tile-total-value">
              {`${fmtMoney(totalUsd)} USD`}
            </div>
          </div>

          <div className="results-tile-total results-tile-total--target">
            <div className="results-tile-label-muted">
              {hasTarget ? `$Total (${targetCode})` : "Total (Target)"}
            </div>
            <div className="results-tile-total-value">
              {hasTarget ? `${fmtMoney(targetFinal)} ${targetCode}` : "-"}
            </div>
          </div>
        </div>

        {/* Expand details (desktop: fica em baixo na col 1; mobile: linha 5, início/centro consoante CSS) */}
        <div className="results-tile-toggle">
          <button
            type="button"
            onClick={toggle}
            className="results-tile-toggle-btn"
          >
            {t("Detail Cost")} {open ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {/* Expanded details below (containers + breakdown + totals area) */}
      {open && (
        <div className="results-tile-expanded">
          {(offer?.containers || []).map((c, cIdx) => {
            // quick math up front
            const originSub = Number(c?.originUsd ?? 0);
            const freightSub = Number(c?.freightUsd ?? 0);
            const destSub = Number(c?.destinationUsd ?? 0);
            const final = Number(c?.finalUsd ?? 0);

            return (
              <div key={cIdx} className="results-tile-container">
                {/* small header: container iso + qty + final on the right */}
                <div className="results-tile-container-header">
                  <span>
                    {c?.containerIso || "-"}: {c?.quantity ?? 1} {t("Units")}
                  </span>
                  <span className="results-tile-container-final">
                    {t("Final")}: {fmtMoney(final)} USD
                  </span>
                </div>

                {/* three buckets; desktop em grid, mobile empilhado em coluna */}
                <div className="results-tile-container-grid">
                  {/* Origin */}
                  <div className="results-tile-bucket">
                    <div className="results-tile-bucket-title">
                      {t("Origin amounts")}
                    </div>
                    {(c?.originItems || []).map((it, i) => (
                      <div key={i} className="results-tile-charge-row">
                        <div>
                          {it?.chargeName}{" "}
                          <span className="results-tile-charge-code">
                            ({it?.chargeCode})
                          </span>
                        </div>
                        <div>
                          {"USD"} {fmtMoney(it?.amountUsd)}
                        </div>
                      </div>
                    ))}
                    <div className="results-tile-bucket-subtotal">
                      {t("Sub-Total")}: {fmtMoney(originSub)} USD
                    </div>
                  </div>

                  {/* Freight */}
                  <div className="results-tile-bucket">
                    <div className="results-tile-bucket-title">
                      {t("Freight amounts")}
                    </div>
                    {(c?.freightItems || []).map((it, i) => (
                      <div key={i} className="results-tile-charge-row">
                        <div>
                          {it?.chargeName}{" "}
                          <span className="results-tile-charge-code">
                            ({it?.chargeCode})
                          </span>
                        </div>
                        <div>
                          {"USD"} {fmtMoney(it?.amountUsd)}
                        </div>
                      </div>
                    ))}
                    <div className="results-tile-bucket-subtotal">
                      {t("Sub-Total")}: {fmtMoney(freightSub)} USD
                    </div>
                  </div>

                  {/* Destination */}
                  <div className="results-tile-bucket">
                    <div className="results-tile-bucket-title">
                      {t("Destination amounts")}
                    </div>
                    {(c?.destinationItems || []).map((it, i) => (
                      <div key={i} className="results-tile-charge-row">
                        <div>
                          {it?.chargeName}{" "}
                          <span className="results-tile-charge-code">
                            ({it?.chargeCode})
                          </span>
                        </div>
                        <div>
                          {"USD"} {fmtMoney(it?.amountUsd)}
                        </div>
                      </div>
                    ))}
                    <div className="results-tile-bucket-subtotal">
                      {t("Sub-Total")}: {fmtMoney(destSub)} USD
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Totals + markup grid (4 columns desktop)
              Phone: lado esquerdo (raw + fx), lado direito (markup + final) */}
          <div className="results-tile-totals-grid">
            {(() => {
              const totalUsd2 = Number(
                offer?.finalWithMarkupUsd ?? offer?.totalFinalUsd ?? 0
              );
              const rate = Number(offer?.usdToTargetRate ?? 0);
              const code = offer?.targetCurrencyCode || "";

              const explicitTarget = Number(
                offer?.finalInTargetAfterMarkup ?? 0
              );
              const calcTarget = rate && totalUsd2 ? totalUsd2 * rate : 0;
              const finalTarget =
                explicitTarget > 0 ? explicitTarget : calcTarget;
              const hasTarget2 = finalTarget > 0 && !!code;

              const inflatedUsd = Number(offer?.markupAmountUsd ?? 0);
              const inflatedTarget =
                inflatedUsd && rate ? inflatedUsd * rate : 0;

              return (
                <>
                  {/* 1. raw buckets */}
                  <div className="results-tile-total-block results-tile-total-block--raw">
                    <div>
                      <b>{t("Origin") || "Origin"}:</b>{" "}
                      {fmtMoney(offer?.totalOriginUsd)} USD
                    </div>
                    <div>
                      <b>{t("Freight") || "Freight"}:</b>{" "}
                      {fmtMoney(offer?.totalFreightUsd)} USD
                    </div>
                    <div>
                      <b>{t("Destination") || "Destination"}:</b>{" "}
                      {fmtMoney(offer?.totalDestinationUsd)} USD
                    </div>
                  </div>

                  {/* 2. fx and fee */}
                  <div className="results-tile-total-block results-tile-total-block--fx">
                    <div>
                      <b>{t("Target currency") || "Target currency"}:</b>{" "}
                      {code || "-"}
                    </div>
                    <div>
                      <b>{t("Market value rate") || "Market value rate"}:</b>{" "}
                      {rate ? `${fmtMoney(rate)} ${code}/USD` : "-"}
                    </div>
                    <div>
                      <b>{t("Service Fee %") || "Service Fee %"}:</b>{" "}
                      {fmtMoney(offer?.markupPercentApplied)}
                    </div>
                  </div>

                  {/* 3. markup box */}
                  <div className="results-tile-total-block results-tile-total-block--markup">
                    <div>
                      <b>{t("Total USD") || "Total USD"}:</b>{" "}
                      {fmtMoney(offer?.totalFinalUsd)} USD
                    </div>
                    <div>
                      <b>{t("Inflated amount") || "Inflated amount"} (USD):</b>{" "}
                      {fmtMoney(inflatedUsd)} USD
                    </div>
                    <div>
                      <b>
                        {t("Inflated amount") || "Inflated amount"} (
                        {code || t("Target") || "Target"}):
                      </b>{" "}
                      {rate ? `${fmtMoney(inflatedTarget)} ${code}` : "-"}
                    </div>
                  </div>

                  {/* 4. final totals */}
                  <div className="results-tile-total-block results-tile-total-block--final">
                    <div className="results-tile-final-line">
                      {t("Final (USD)") || "Final (USD)"}:{" "}
                      <span className="results-tile-final-value">
                        {fmtMoney(offer?.finalWithMarkupUsd)} USD
                      </span>
                    </div>
                    <div className="results-tile-final-line">
                      {t(`Final (${code || "Target"})`)}:{" "}
                      <span className="results-tile-final-value">
                        {hasTarget2 ? `${fmtMoney(finalTarget)} ${code}` : "-"}
                      </span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

ResultsTile.propTypes = {
  offer: PropTypes.object, // the single offer payload (normalized or raw-shaped)
  index: PropTypes.number, // optional index for keys in lists
  t: PropTypes.func, // i18n translator
  onSave: PropTypes.func, // save handler provided by parent
  viewMode: PropTypes.bool, // when true, hides the Save button (read-only/view mode)
};
